import { useEffect, useRef, useState } from 'react';
import type { ResizeHandle } from '@/components/canvas/transform';
import { applyAspectRatio } from '@/components/canvas/transform/useResize';
import type { Page } from '@/components/canvas/document/publicTypes';

const MIN_PAGE_SIZE = 80;

export interface PageResizePreview {
  width: number;
  height: number;
  workspaceX: number;
  workspaceY: number;
}

interface PageResizeState {
  handle: ResizeHandle;
  startW: number;
  startH: number;
  startLayoutX: number;
  startLayoutY: number;
  startClientX: number;
  startClientY: number;
  elementStarts: { id: string; x: number; y: number }[];
}

export function usePageResize(
  zoom: number,
  getPage: () => Page | null,
  applyPreview: (preview: PageResizePreview, elementStarts: PageResizeState['elementStarts'], localOffset: { x: number; y: number }) => void,
  commitPreview: (preview: PageResizePreview, elementStarts: PageResizeState['elementStarts'], localOffset: { x: number; y: number }) => void,
  pushHistory: () => void,
) {
  const [pageResizeState, setPageResizeState] = useState<PageResizeState | null>(null);
  const [pageResizePreview, setPageResizePreview] = useState<PageResizePreview | null>(null);
  const previewRef = useRef<PageResizePreview | null>(null);
  const localOffsetRef = useRef({ x: 0, y: 0 });
  const elementStartsRef = useRef<PageResizeState['elementStarts']>([]);

  const startPageResize = (handle: ResizeHandle, e: React.MouseEvent) => {
    const page = getPage();
    if (!page) return;
    e.stopPropagation();
    e.preventDefault();
    pushHistory();
    const elementStarts = page.elements.map((el) => ({
      id: el.id,
      x: el.layout.x,
      y: el.layout.y,
    }));
    elementStartsRef.current = elementStarts;
    setPageResizeState({
      handle,
      startW: page.size.width,
      startH: page.size.height,
      startLayoutX: page.workspaceLayout.x,
      startLayoutY: page.workspaceLayout.y,
      startClientX: e.clientX,
      startClientY: e.clientY,
      elementStarts,
    });
    const initial: PageResizePreview = {
      width: page.size.width,
      height: page.size.height,
      workspaceX: page.workspaceLayout.x,
      workspaceY: page.workspaceLayout.y,
    };
    previewRef.current = initial;
    localOffsetRef.current = { x: 0, y: 0 };
    setPageResizePreview(initial);
  };

  useEffect(() => {
    if (!pageResizeState) return;
    const {
      handle,
      startW,
      startH,
      startLayoutX,
      startLayoutY,
      startClientX,
      startClientY,
      elementStarts,
    } = pageResizeState;

    const onMove = (e: MouseEvent) => {
      const dCanvasX = (e.clientX - startClientX) / zoom;
      const dCanvasY = (e.clientY - startClientY) / zoom;
      let localX = 0;
      let localY = 0;
      let w = startW;
      let h = startH;

      if (handle === 'nw') {
        w = Math.max(MIN_PAGE_SIZE, startW - dCanvasX);
        h = Math.max(MIN_PAGE_SIZE, startH - dCanvasY);
        localX = startW - w;
        localY = startH - h;
      } else if (handle === 'n') {
        h = Math.max(MIN_PAGE_SIZE, startH - dCanvasY);
        localY = startH - h;
      } else if (handle === 'ne') {
        w = Math.max(MIN_PAGE_SIZE, startW + dCanvasX);
        h = Math.max(MIN_PAGE_SIZE, startH - dCanvasY);
        localY = startH - h;
      } else if (handle === 'e') {
        w = Math.max(MIN_PAGE_SIZE, startW + dCanvasX);
      } else if (handle === 'se') {
        w = Math.max(MIN_PAGE_SIZE, startW + dCanvasX);
        h = Math.max(MIN_PAGE_SIZE, startH + dCanvasY);
      } else if (handle === 's') {
        h = Math.max(MIN_PAGE_SIZE, startH + dCanvasY);
      } else if (handle === 'sw') {
        w = Math.max(MIN_PAGE_SIZE, startW - dCanvasX);
        h = Math.max(MIN_PAGE_SIZE, startH + dCanvasY);
        localX = startW - w;
      } else if (handle === 'w') {
        w = Math.max(MIN_PAGE_SIZE, startW - dCanvasX);
        localX = startW - w;
      }

      if (e.shiftKey) {
        const ar = applyAspectRatio(handle, startW, startH, localX, localY, w, h);
        localX = ar.x;
        localY = ar.y;
        w = ar.w;
        h = ar.h;
      }

      const preview: PageResizePreview = {
        width: w,
        height: h,
        workspaceX: startLayoutX + localX,
        workspaceY: startLayoutY + localY,
      };
      previewRef.current = preview;
      localOffsetRef.current = { x: localX, y: localY };
      setPageResizePreview(preview);
      applyPreview(preview, elementStarts, localOffsetRef.current);
    };

    const onUp = () => {
      if (previewRef.current) {
        commitPreview(previewRef.current, elementStartsRef.current, localOffsetRef.current);
      }
      setPageResizeState(null);
      setPageResizePreview(null);
      previewRef.current = null;
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [pageResizeState, zoom, applyPreview, commitPreview]);

  return { pageResizePreview, startPageResize };
}
